import React, { type FC, useEffect } from 'react'

import { selectServiceDeviationPage } from '../../../domain'

import { useAppState, useAppUsecase } from '../State'
import { selectCanReadDeviations } from './Badge'
import { DeviationToastZone } from './ToastZone'

/**
 * Keeps the two existing subscriptions alive and feeds `DeviationToastsUsecase` from them - no
 * visible output of its own; `DeviationToastZone` (rendered by the parent) is what actually shows
 * anything, off the app state this usecase maintains.
 */
const DeviationsDetector: FC = () => {
    const page = useAppState(selectServiceDeviationPage)
    const resolution = useAppState((s) => s.serviceDeviationResolution)
    const manageServicesDeviationsUsecase = useAppUsecase('manageServicesDeviationsUsecase')
    const manageResolvedDeviationsUsecase = useAppUsecase('manageResolvedDeviationsUsecase')
    const deviationToastsUsecase = useAppUsecase('deviationToastsUsecase')

    useEffect(() => manageServicesDeviationsUsecase.subscribe({ short: true }), [manageServicesDeviationsUsecase])
    useEffect(() => manageResolvedDeviationsUsecase.subscribe(), [manageResolvedDeviationsUsecase])

    useEffect(() => {
        if (page) {
            deviationToastsUsecase.syncDeviations(page.current, page.totalCount)
        }
    }, [page, deviationToastsUsecase])

    /**
     * Skips (rather than falling back to 0) when `page` hasn't loaded yet - a resolution event can
     * arrive before the deviations-page subscription's first snapshot on an ordinary mount, and 0
     * would incorrectly read as "no deviations left" and clear the counter toast. Deliberately not
     * keyed on `page` in the dependency array - only a new resolution should trigger this at all;
     * `page` is only read as a guard against the value it happens to be at that moment.
     */
    useEffect(() => {
        if (resolution && page) {
            deviationToastsUsecase.notifyResolved(resolution, page.totalCount)
        }
    }, [resolution, deviationToastsUsecase])

    return null
}

export const Notifications: FC = () => {
    const canReadDeviations = useAppState(selectCanReadDeviations)

    return canReadDeviations
        ? (
            <>
                <DeviationsDetector />
                <DeviationToastZone />
            </>
        )
        : null
}
